import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Progress, Button } from 'reactstrap';
import { config } from '../../config/environment';

const PaymentVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'timeout' | 'error'>('verifying');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('');

  const flowId = searchParams.get('flow');
  const provider = searchParams.get('provider') || 'stripe';
  const maxWaitTime = provider === 'razorpay' ? 300 : 120;

  const checkFlowStatus = useCallback(async () => {
    try {
      const response = await fetch(`${config.api.onboardingFlows}/${flowId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const flow = await response.json();
        return flow;
      } else {
        throw new Error(`Failed to check flow status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching flow status:', error);
      throw error;
    }
  }, [flowId]);

  useEffect(() => {
    if (elapsedTime < 30) {
      setCurrentMessage('Processing your payment...');
    } else if (elapsedTime < 120) {
      setCurrentMessage('Verifying payment details...');
    } else {
      setCurrentMessage('Nearly there, finalizing your subscription...');
    }
  }, [elapsedTime]);

  const startVerificationProcess = useCallback(async () => {
    const startTime = Date.now();
    const pollInterval = setInterval(async () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);

      if (elapsed >= maxWaitTime) {
        clearInterval(pollInterval);
        setVerificationStatus('timeout');
        return;
      }

      try {
        const flowData = await checkFlowStatus();
        if (flowData && flowData.status === 'PAID') {
          clearInterval(pollInterval);
          navigate(`/create-organization?flow=${flowId}`);
          return;
        }
      } catch (error) {
        console.error('Error checking flow status:', error);
        if (elapsed > 60) {
          clearInterval(pollInterval);
          setVerificationStatus('error');
        }
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [navigate, flowId, maxWaitTime, checkFlowStatus]);

  useEffect(() => {
    if (!flowId) {
      console.error('No flow ID provided for payment verification');
      setVerificationStatus('error');
      return;
    }

    let cleanup: (() => void) | undefined;

    const initVerification = async () => {
      cleanup = await startVerificationProcess();
    };

    initVerification();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [flowId, startVerificationProcess]);

  const handleRetry = () => {
    setVerificationStatus('verifying');
    setElapsedTime(0);
    startVerificationProcess();
  };

  const handleContactSupport = () => {
    window.open('mailto:support@mentorgain.com?subject=Payment Verification Issue&body=Flow ID: ' + flowId, '_blank');
  };

  const getProgressPercentage = () => {
    return Math.min((elapsedTime / maxWaitTime) * 100, 95);
  };

  const getProviderName = () => {
    return provider === 'razorpay' ? 'Razorpay' : 'Stripe';
  };

  if (verificationStatus === 'verifying') {
    return (
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="onboarding-card-wrapper">
              <Card className="border-0 shadow-sm">
                <CardBody className="p-5 text-center">
                  <div className="mb-4">
                    <div className="mb-3">
                      <div className="onboarding-icon onboarding-icon--primary onboarding-icon--lg">
                        <i className="fa-solid fa-credit-card" />
                      </div>
                    </div>
                    <h3 className="fw-bold mb-2">Setting up your subscription</h3>
                    <p className="text-muted mb-0">{currentMessage}</p>
                  </div>

                  <div className="mb-4">
                    <Progress
                      value={getProgressPercentage()}
                      color="primary"
                      className="mb-2"
                      style={{ height: '8px', borderRadius: '4px' }}
                    />
                    <small className="text-muted">
                      <i className="fa-solid fa-lock me-1" />
                      Processing via {getProviderName()}... ({elapsedTime}s)
                    </small>
                  </div>

                  <div className="text-start">
                    <div className="d-flex align-items-center mb-3">
                      <div className="me-3">
                        <i className="fa-solid fa-circle-check text-success" />
                      </div>
                      <small className="text-muted">Payment initiated successfully</small>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="me-3">
                        <div className="spinner-ring spinner-ring--sm" />
                      </div>
                      <small className="text-muted">Verifying payment with {getProviderName()}</small>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <i className="fa-regular fa-circle text-muted" style={{ opacity: 0.4 }} />
                      </div>
                      <small className="text-muted">Preparing your workspace</small>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-top">
                    <small className="text-muted">
                      <i className="fa-solid fa-shield-halved me-1" />
                      This usually takes less than a minute. Please don't refresh the page.
                    </small>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'timeout') {
    return (
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="onboarding-card-wrapper">
              <Card className="border-0 shadow-sm">
                <CardBody className="p-5 text-center">
                  <div className="mb-4">
                    <div className="onboarding-icon onboarding-icon--warning onboarding-icon--lg">
                      <i className="fa-solid fa-clock" />
                    </div>
                  </div>
                  <h4 className="fw-bold mb-3">Payment verification taking longer than usual</h4>
                  <p className="text-muted mb-4">
                    Your payment is being processed, but it's taking longer than expected.
                    This sometimes happens with {getProviderName()} transactions.
                  </p>
                  <div className="d-flex gap-2 justify-content-center mb-4">
                    <Button color="warning" size="sm" onClick={handleRetry}>
                      <i className="fa-solid fa-rotate me-1" /> Check Again
                    </Button>
                    <Button outline color="secondary" size="sm" onClick={handleContactSupport}>
                      <i className="fa-solid fa-envelope me-1" /> Contact Support
                    </Button>
                  </div>
                  <div className="text-muted small p-3 bg-light rounded-3">
                    <strong>What's happening:</strong><br />
                    Your payment was submitted successfully, but we're still waiting for confirmation
                    from {getProviderName()}. This can take a few minutes during high traffic periods.
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="onboarding-card-wrapper">
              <Card className="border-0 shadow-sm">
                <CardBody className="p-5 text-center">
                  <div className="mb-4">
                    <div className="onboarding-icon onboarding-icon--error onboarding-icon--lg">
                      <i className="fa-solid fa-triangle-exclamation" />
                    </div>
                  </div>
                  <h4 className="fw-bold mb-3">Unable to verify payment</h4>
                  <p className="text-muted mb-4">
                    We're having trouble connecting to our payment verification system.
                  </p>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <Button color="primary" size="sm" onClick={handleRetry}>
                      <i className="fa-solid fa-rotate me-1" /> Try Again
                    </Button>
                    <Button outline color="secondary" size="sm" onClick={() => navigate(`/getting-started?flow=${flowId}`)}>
                      <i className="fa-solid fa-arrow-left me-1" /> Back to Plans
                    </Button>
                    <Button outline color="secondary" size="sm" onClick={handleContactSupport}>
                      <i className="fa-solid fa-circle-question me-1" /> Get Help
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PaymentVerification;
